import React, { useState } from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { Button } from '../primitives';

/**
 * Composed ActionsButton component
 * Dropdown button that opens a menu of configurable actions
 *
 * Each action supports:
 *   label      - text shown in the menu (required)
 *   onClick    - handler invoked when the action is selected
 *   id         - optional stable key
 *   icon       - optional element rendered before the label
 *   disabled   - optional, renders the item non-interactive
 *   hidden     - optional, omits the item entirely
 *   divider    - optional, renders a separator above the item
 */
const ActionsButton = ({
  label = 'Actions',
  actions = [],
  variant = 'contained',
  color = 'primary',
  disabled = false,
  menuProps = {},
  sx = {},
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const visibleActions = actions.filter((action) => action && !action.hidden);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleActionClick = (action) => (event) => {
    setAnchorEl(null);
    if (action.onClick) {
      action.onClick(event);
    }
  };

  return (
    <>
      <Button
        variant={variant}
        color={color}
        disabled={disabled || visibleActions.length === 0}
        onClick={handleOpen}
        endIcon={<KeyboardArrowDown />}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        sx={sx}
        {...props}
      >
        {label}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        {...menuProps}
      >
        {visibleActions.map((action, index) => {
          const key = action.id || action.label || index;
          return [
            action.divider && index > 0 ? <Divider key={`${key}-divider`} /> : null,
            <MenuItem
              key={key}
              onClick={handleActionClick(action)}
              disabled={action.disabled}
            >
              {action.icon && <ListItemIcon>{action.icon}</ListItemIcon>}
              <ListItemText>{action.label}</ListItemText>
            </MenuItem>,
          ];
        })}
      </Menu>
    </>
  );
};

export default ActionsButton;
